import { useMemo, useState } from 'react';
import type { FilterGroup } from '@practium/ui';
import { COFFEE_OPTIONS, RANGE_MODE_LABELS } from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type { RangeCombination, RangeMode, RangesKind, ReferenceSectionProps } from '../../types/parameters.types';
import { listRangeCombinations, removeRangeCombination } from '../../utils/rangeCombinations';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';
import { RangeCombinationModal } from './RangeCombinationModal';

interface RangesSectionProps extends ReferenceSectionProps {
	kind: RangesKind;
}

type ModalState = { type: 'closed' } | { type: 'open'; combination: RangeCombination | null };

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Tipo de café',
		options: COFFEE_OPTIONS.map((option) => ({ value: `coffee:${option.value}`, label: option.label })),
	},
	{
		label: 'Organização',
		options: (Object.keys(RANGE_MODE_LABELS) as RangeMode[]).map((value) => ({
			value: `mode:${value}`,
			label: RANGE_MODE_LABELS[value],
		})),
	},
];

const getSearchText = (item: RangeCombination) => `${item.title} ${item.context}`;

const getFilterTags = (item: RangeCombination) => [`coffee:${item.coffeeType}`, `mode:${item.mode}`];

export const RangesSection = ({ kind, reference, isSaving, onSave, onConfirmDelete }: RangesSectionProps) => {
	const key = kind === 'foliar' ? 'criticalRanges' : 'drisParams';
	const profiles = reference[key];
	const [modal, setModal] = useState<ModalState>({ type: 'closed' });
	const combinations = useMemo(() => listRangeCombinations(profiles, key), [profiles, key]);
	const list = useTableList({ items: combinations, filterGroups: FILTER_GROUPS, getSearchText, getFilterTags });

	const columns = useMemo<ReferenceColumn<RangeCombination>[]>(
		() => [
			{
				key: 'coffee',
				header: 'Tipo de café',
				render: (item) => <span className={tableStyles.cellPrimary}>{item.title}</span>,
			},
			{
				key: 'mode',
				header: 'Organização',
				render: (item) => <span className={tableStyles.cellSecondary}>{RANGE_MODE_LABELS[item.mode]}</span>,
			},
			{
				key: 'context',
				header: 'Contexto',
				render: (item) => <span className={tableStyles.cellSecondary}>{item.context}</span>,
			},
			{
				key: 'count',
				header: kind === 'foliar' ? 'Nutrientes' : 'Relações',
				render: (item) => <span className={tableStyles.cellSecondary}>{item.ranges.length}</span>,
			},
		],
		[kind]
	);

	const handleDelete = async (item: RangeCombination) => {
		if (await onConfirmDelete(`a combinação ${item.title} · ${item.context}`)) {
			await onSave({ [key]: removeRangeCombination(profiles, item) }, 'Combinação excluída com sucesso!');
		}
	};

	return (
		<>
			<SectionList
				list={list}
				columns={columns}
				getRowId={(item) => item.id}
				unit={['combinação', 'combinações']}
				searchPlaceholder="Buscar por café, região ou estação..."
				actionLabel="Nova combinação"
				onAction={() => setModal({ type: 'open', combination: null })}
				onEdit={(combination) => setModal({ type: 'open', combination })}
				onDelete={handleDelete}
			/>
			{modal.type === 'open' && (
				<RangeCombinationModal
					kind={kind}
					reference={reference}
					combinations={combinations}
					combination={modal.combination}
					isSaving={isSaving}
					onClose={() => setModal({ type: 'closed' })}
					onSubmit={(next) => onSave({ [key]: next }, 'Combinação salva com sucesso!')}
				/>
			)}
		</>
	);
};
